/**
 * What a patient is told while moving an appointment — decided by the server,
 * worded here.
 *
 * `GET /appointments/:id/reschedule-options` answers with everything the
 * patient may do (blhWebServer docs/AVAILABILITY_CONTRACT.md §2): the days and
 * their times, why a day has none, the clinicians this visit may move to
 * (`meta.clinicians`), the notice the clinic asks for (`meta.notice`), and the
 * clinic's zone (`meta.timeZone`). The portal used to decide two of those
 * itself — which clinicians were eligible, and a fixed "24 hours from now" —
 * and so could disagree with the clinic that set the number. Nothing here
 * decides; it only says what the server decided, in words a patient reads.
 */

import {APP_LOCALE} from "../../utils/appLocale";
import {deliveryModeOfAppointment, isRemote} from "./deliveryMode";

// ── why a day has no times ─────────────────────────────────────────────────
//
// The server speaks the availability model v2 (time_off, closed, too_soon…);
// older deployments spoke v1 (no_shift, clinician_unavailable, outside_shift).
// The same fact gets the same words in either. "No hours" never means the
// clinic changed anything — most often nobody set the hours up — so the patient
// is told what is true for them: nothing is open online that day.

const NO_ONLINE = {title: "No online times", detail: "There are no times to book online that day."};
const AWAY = {title: "Not available that day", detail: "Your clinician is away that day."};

const CLOSED_COPY = Object.freeze({
  fully_booked: {title: "Fully booked", detail: "Every time that day has been taken."},
  held: {title: "Not available that day", detail: "Those times are no longer open."},
  day_passed: {title: "That day has passed", detail: "Please pick a day from today on."},
  time_off: AWAY,
  clinician_unavailable: AWAY,
  closed: {title: "The clinic is closed", detail: "The clinic is closed that day."},
  too_soon: {title: "Too soon to change online", detail: "That day is inside the clinic's notice period."},
  too_far: {title: "Not open for booking yet", detail: "That day is too far ahead to book online."},
  outside_hours: NO_ONLINE,
  no_hours: NO_ONLINE,
  not_configured: NO_ONLINE,
  no_shift: NO_ONLINE,
  outside_shift: NO_ONLINE,
});

const UNKNOWN_CLOSED = {title: "No times that day", detail: "There are no open times that day."};

/** The words for a closed day. A reason this build does not know is never blank. */
export function closedCopyFor(reason) {
  return reason && Object.prototype.hasOwnProperty.call(CLOSED_COPY, reason) ? CLOSED_COPY[reason] : UNKNOWN_CLOSED;
}

// Most informative first — the server's own order (engine REASON_PRECEDENCE),
// with v1's words where they mean the same thing.
const PRECEDENCE = [
  "fully_booked",
  "held",
  "time_off",
  "clinician_unavailable",
  "closed",
  "too_soon",
  "too_far",
  "outside_hours",
  "outside_shift",
  "no_hours",
  "no_shift",
  "not_configured",
  "day_passed",
];

/** The reason to tell a patient when none of these days has a time. */
export function mostInformativeReason(days) {
  const reasons = Object.values(days || {})
    .filter((day) => !(day?.slots || []).length)
    .map((day) => day?.closed?.reason)
    .filter(Boolean);
  if (!reasons.length) return null;
  return reasons.reduce((best, reason) => {
    const a = PRECEDENCE.indexOf(best);
    const b = PRECEDENCE.indexOf(reason);
    if (a === -1) return reason;
    if (b === -1) return best;
    return b < a ? reason : best;
  });
}

/**
 * What one calendar day holds for this clinician, as the server answered it.
 *
 *   {open: true, count}             times to pick
 *   {open: false, reason, full}     why not (`full` when the day was worked
 *                                   and taken — the one closure worth a mark)
 *   {open: false, reason: null}     not answered yet (still loading)
 *
 * A day before today is past whatever the server said about it.
 */
export function dayStatus(day, {did = null, isPast = false, loaded = true} = {}) {
  if (isPast) return {open: false, count: 0, reason: "day_passed", full: false};
  if (!day) return {open: false, count: 0, reason: loaded ? "unknown" : null, full: false};
  const count = (day.slots || []).filter((slot) => !did || slot.did === did).length;
  if (count > 0) return {open: true, count, reason: null, full: false};
  // A day with times only for OTHER clinicians is full for this one.
  const reason = day.closed?.reason || ((day.slots || []).length ? "fully_booked" : "unknown");
  return {open: false, count: 0, reason, full: reason === "fully_booked" || reason === "held"};
}

/** The first day on or after `from` with a time for this clinician, or null. */
export function firstOpenDay(days, {did = null, from}) {
  return (
    Object.keys(days || {})
      .filter((key) => key >= from)
      .sort()
      .find((key) => dayStatus(days[key], {did}).open) || null
  );
}

/** "Changes need at least 24 hours' notice." — or null when the clinic asks for none. */
export function noticeSentence(notice) {
  const hours = Number(notice?.minimumNoticeHours);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `Changes need at least ${days} ${days === 1 ? "day's" : "days'"} notice.`;
  }
  return `Changes need at least ${hours} ${hours === 1 ? "hour's" : "hours'"} notice.`;
}

// ── refusals ───────────────────────────────────────────────────────────────

/**
 * What the server said when it refused, ready to show: its own sentence and,
 * when it gave one, the clinic's phone. Null for a failure that is not an
 * answer (the network, the server failing) — the caller says "try again".
 */
export function refusalFrom(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (!status || status >= 500) return null;
  const message = data?.error?.message || data.message || data.blockedMessage || null;
  if (!message) return null;
  return {message, clinicPhone: data.clinicPhone || null, code: typeof data.error === "string" ? data.error : null};
}

// ── clocks ─────────────────────────────────────────────────────────────────
//
// An appointment is at its location's hour, for everyone: every time is shown
// on the clinic's clock, named. For a VIRTUAL visit the patient may be
// somewhere else entirely, so their own clock is shown beside it — smaller,
// never instead (blhWebServer docs/AVAILABILITY_MODEL_V2.md §7).

/** "Toronto time" from "America/Toronto"; the zone id itself when it has no city. */
export function zoneLabel(timeZone) {
  if (!timeZone) return "your time";
  const city = String(timeZone).split("/").pop();
  return `${city.replace(/_/g, " ")} time`;
}

/** "9:30 a.m." on a given clock (the app's language, never the reader's). */
export function timeOn(ms, timeZone) {
  if (!Number.isFinite(Number(ms))) return "";
  return new Date(Number(ms)).toLocaleTimeString(APP_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? {timeZone} : {}),
  });
}

/** "Tuesday, October 6" on a given clock. */
export function dayOn(ms, timeZone) {
  if (!Number.isFinite(Number(ms))) return "";
  return new Date(Number(ms)).toLocaleDateString(APP_LOCALE, {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(timeZone ? {timeZone} : {}),
  });
}

/**
 * A day's times as morning, afternoon and evening on the CLINIC's clock — how
 * a person scans thirty times for the one that fits their day. Empty parts
 * are left out; order is kept.
 */
export function periodsOf(slots, timeZone) {
  const hourOf = (ms) =>
    Number(
      new Intl.DateTimeFormat("en-CA", {hour: "numeric", hourCycle: "h23", ...(timeZone ? {timeZone} : {})})
        .formatToParts(new Date(Number(ms)))
        .find((part) => part.type === "hour")?.value
    );
  const periods = [
    {period: "Morning", slots: []},
    {period: "Afternoon", slots: []},
    {period: "Evening", slots: []},
  ];
  (slots || []).forEach((slot) => {
    const hour = hourOf(slot.start);
    periods[hour < 12 ? 0 : hour < 17 ? 1 : 2].slots.push(slot);
  });
  return periods.filter((entry) => entry.slots.length > 0);
}

/** The reader's own zone, or null when the browser will not say. */
export function viewerTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** The calendar day ("2026-10-06") an instant falls on, on a given clock. */
export function dayKeyOn(ms, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone ? {timeZone} : {}),
  }).formatToParts(new Date(Number(ms)));
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Whether the patient's own clock is worth showing beside the clinic's: only
 * for a virtual visit, and only when the two clocks actually read differently
 * at that instant (Toronto and New York share one; the zone names do not
 * matter, the hour on the wall does).
 */
export function showsTwoClocks({isVirtual, clinicTimeZone, viewerZone = viewerTimeZone(), at = Date.now()}) {
  if (!isVirtual || !clinicTimeZone || !viewerZone) return false;
  const wall = (zone) =>
    new Date(Number(at)).toLocaleString("en-CA", {timeZone: zone, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"});
  try {
    return wall(clinicTimeZone) !== wall(viewerZone);
  } catch {
    return false;
  }
}

/** A visit the patient joins from wherever they are: video or phone (§2.7). */
export function isVirtualVisit(appointment) {
  return isRemote(deliveryModeOfAppointment(appointment));
}
